// ============================================================
// TOUR TYPES
// ============================================================

import {
  type ToursSubscriptionTier as ToursSubscriptionTierBase,
  type ToursPlanType as ToursPlanTypeBase,
  type HostingPlanId,
  TOUR_SUBSCRIPTION_TIERS as TOUR_SUBSCRIPTION_TIERS_IMPORT,
  HOSTING_PLANS as HOSTING_PLANS_IMPORT,
} from '@rehub/pricing';

export type HostingPlan = HostingPlanId;
export type HostingStatus = 'active' | 'inactive' | 'expired';
export type ToursSubscriptionTier = ToursSubscriptionTierBase;
export type ToursPlanType = ToursPlanTypeBase;
export type TourBuildMode = 'DIY' | 'STUDIO' | 'AUTO';
export type StudioBuildStatus = 'draft' | 'paid' | 'queued' | 'in_progress' | 'qc' | 'delivered';

/**
 * Top-level tour experience type.
 *   - 'property'    : standard real-estate 360 tour (default, unchanged behaviour).
 *   - 'venue'       : admin-built venue experience (directory, amenities,
 *                     visitor panel) for shopping centres, museums, etc.
 *   - 'hospitality' : pubs, restaurants, bars, hotels, cafés, wedding venues.
 *                     Reuses Venue Mode foundations with its own terminology,
 *                     tables, specials board and booking CTAs.
 *   - 'adventure'   : progression-based adventure/escape-room experience.
 *                     Scenes become rooms unlocked by puzzles, items and
 *                     conditions (see apps/tours/adventure).
 */
export type TourMode = 'property' | 'venue' | 'hospitality' | 'adventure';

export const TOUR_SUBSCRIPTION_TIERS = TOUR_SUBSCRIPTION_TIERS_IMPORT;
export const HOSTING_PLANS = HOSTING_PLANS_IMPORT;

export interface Tour {
    id: string;
    user_id: string;
    team_id: string | null;
    title: string;
    description: string | null;
    /** Internal job/reference number for quick search (e.g. TR-001). */
    internal_job_number?: string | null;
    slug: string;
    /** Short 6-character access code for the Meta Quest VR viewer. */
    vr_code?: string | null;
    status: 'draft' | 'published' | 'archived';
    cover_image_url: string | null;
    settings: TourSettings;
    password: string | null;
    custom_domain: string | null;
    custom_branding: CustomBranding;
    property_id: string | null;
    // Hosting duration fields
    hosting_plan: HostingPlan | null;
    hosting_expires_at: string | null;
    hosting_status: HostingStatus;
    hosting_permanent: boolean;
    // Studio build
    build_mode?: TourBuildMode;
    is_rapid?: boolean;
    /** Tour experience type. Defaults to 'property'. 'venue' enables Venue Mode. */
    tour_mode: TourMode;
    // Brand profile link
    brand_profile_id: string | null;
    // Sub-brand link (per-tour override of the user's active sub-brand)
    sub_brand_id?: string | null;
    // Location
    location_address: string | null;
    location_lat: number | null;
    location_lng: number | null;
    // Meta
    view_count: number;
    created_at: string;
    updated_at: string;
}

export interface CreditHistoryEntry {
    id: string;
    user_id: string;
    tour_id: string | null;
    action: string;
    credits_delta: number;
    balance_after: number;
    metadata: Record<string, any>;
    created_at: string;
}

export type HotspotStylePreset = 'pulsing_ring' | 'pulsing_glow' | 'breathing_circle' | 'arrow_bounce' | 'arrow_away' | 'minimal_dot' | 'floating_label' | 'circle_icon';

/** When 'default', arrow/door use 360Tours/brand icon. When 'chosen', use the icon selected per hotspot (arrow SVG, door SVG, etc.). */
export type HotspotIconMode = 'default' | 'chosen';

export const HOTSPOT_ICON_MODE_PRESETS: { id: HotspotIconMode; label: string; description: string }[] = [
    { id: 'default', label: 'Default 360Tours icon', description: 'Arrow and door hotspots always show the 360Tours icon (your brand logo is not used for hotspots).' },
    { id: 'chosen', label: 'Chosen per hotspot', description: 'Each hotspot shows the icon you pick (Arrow, Door, Logo, Info, etc.). Pick Logo to use your brand logo.' },
];

export const HOTSPOT_STYLE_PRESETS: { id: HotspotStylePreset; label: string; description: string }[] = [
    { id: 'pulsing_ring', label: 'Pulsing Ring', description: 'Solid centre with ring that pulses outward' },
    { id: 'pulsing_glow', label: 'Pulsing Glow', description: 'Circle with breathing glow animation' },
    { id: 'breathing_circle', label: 'Breathing Circle', description: 'Circle that gently scales in and out' },
    { id: 'arrow_bounce', label: 'Bouncing Arrow', description: 'Arrow that gently bounces' },
    { id: 'arrow_away', label: 'Direction Arrow', description: 'Flat arrow facing away, animates in direction of travel' },
    { id: 'minimal_dot', label: 'Minimal Dot', description: 'Small dot, expands on hover' },
    { id: 'floating_label', label: 'Floating Label', description: 'Room name with pointer' },
    { id: 'circle_icon', label: 'Circle + Icon', description: 'Classic circle with glow animation' },
];

export const HOTSPOT_COLOR_PRESETS = [
    { id: '#f97316', label: 'Orange' },
    { id: '#3b82f6', label: 'Blue' },
    { id: '#8b5cf6', label: 'Purple' },
    { id: '#22c55e', label: 'Green' },
    { id: '#ef4444', label: 'Red' },
    { id: '#ffffff', label: 'White' },
];

/** Hotspot icon options for the editor. "logo" = brand logo when set; "360tours" = app icon; "custom" = upload your own. */
export const HOTSPOT_ICONS: { id: string; label: string }[] = [
    { id: '360tours', label: '360Tours icon' },
    { id: 'arrow', label: 'Arrow' },
    { id: 'door', label: 'Door' },
    { id: 'logo', label: 'Logo' },
    { id: 'stairs', label: 'Stairs' },
    { id: 'info', label: 'Info' },
    { id: 'link', label: 'Link' },
    { id: 'location', label: 'Location' },
    { id: 'video', label: 'Video' },
    { id: 'home', label: 'Home' },
    { id: 'chevron', label: 'Chevron' },
    { id: 'image', label: 'Image' },
    // Thematic content icons — educational worlds, property, hospitality
    { id: 'globe', label: 'Globe' },
    { id: 'leaf', label: 'Plant' },
    { id: 'paw', label: 'Wildlife' },
    { id: 'cloud', label: 'Climate' },
    { id: 'alert', label: 'Alert' },
    { id: 'tree', label: 'Forest' },
    { id: 'mountain', label: 'Mountain' },
    { id: 'droplet', label: 'Water' },
    { id: 'sun', label: 'Sun' },
    { id: 'book', label: 'Learn' },
    { id: 'star', label: 'Feature' },
    { id: 'lightbulb', label: 'Tip' },
    { id: 'users', label: 'People' },
    { id: 'landmark', label: 'Landmark' },
    { id: 'compass', label: 'Explore' },
    { id: 'camera', label: 'Photo' },
    { id: 'heart', label: 'Favourite' },
    { id: 'help', label: 'Help' },
    { id: 'custom', label: 'Custom (upload)' },
];

/** Quick-insert emojis for info hotspot titles / body text. */
export const INFO_HOTSPOT_TEXT_EMOJIS: { emoji: string; label: string }[] = [
    // Core / worlds
    { emoji: '🌍', label: 'Earth / globe' },
    { emoji: '📍', label: 'Overview / location' },
    { emoji: 'ℹ️', label: 'Info' },
    { emoji: '⭐', label: 'Feature / highlight' },
    { emoji: '💡', label: 'Tip / idea' },
    { emoji: '📚', label: 'Learn / facts' },
    { emoji: '🧭', label: 'Explore / navigate' },
    { emoji: '❓', label: 'Question / FAQ' },
    // Nature & biomes
    { emoji: '🌴', label: 'Palm / rainforest' },
    { emoji: '🌿', label: 'Plant life' },
    { emoji: '🌳', label: 'Forest / tree' },
    { emoji: '🐵', label: 'Wildlife' },
    { emoji: '🦁', label: 'Safari / big cats' },
    { emoji: '🐦', label: 'Birds' },
    { emoji: '🐠', label: 'Fish / marine' },
    { emoji: '🪸', label: 'Coral / reef' },
    { emoji: '🌊', label: 'Ocean / water' },
    { emoji: '🏜️', label: 'Desert' },
    { emoji: '❄️', label: 'Tundra / ice' },
    { emoji: '⛰️', label: 'Mountain' },
    { emoji: '🌋', label: 'Volcano' },
    { emoji: '🌦️', label: 'Climate / weather' },
    { emoji: '☀️', label: 'Sun / day' },
    { emoji: '🌙', label: 'Night / moon' },
    { emoji: '⚠️', label: 'Conservation / alert' },
    // Property & hospitality
    { emoji: '🏠', label: 'Home / property' },
    { emoji: '🛏️', label: 'Bedroom' },
    { emoji: '🍳', label: 'Kitchen' },
    { emoji: '🛁', label: 'Bathroom' },
    { emoji: '🛋️', label: 'Living room' },
    { emoji: '🪴', label: 'Garden' },
    { emoji: '🅿️', label: 'Parking' },
    { emoji: '🍽️', label: 'Dining / restaurant' },
    { emoji: '☕', label: 'Cafe' },
    { emoji: '🏨', label: 'Hotel' },
    { emoji: '🏛️', label: 'Landmark / building' },
    { emoji: '📸', label: 'Photo / view' },
    { emoji: '❤️', label: 'Favourite' },
];

/** Transition effect when moving between panoramas (Photo Sphere Viewer). */
export type TransitionEffect = 'fade' | 'black' | 'white';

export const TRANSITION_EFFECT_PRESETS: { id: TransitionEffect; label: string; description: string }[] = [
    { id: 'fade', label: 'Fade', description: 'Smooth crossfade between scenes' },
    { id: 'black', label: 'Black', description: 'Fade to black, then to next scene' },
    { id: 'white', label: 'White', description: 'Fade to white, then to next scene' },
];

/**
 * Public viewer "experience" (chrome / navigation layout). Stored in
 * `tours.settings.viewerStyle` — the column name is kept for backwards
 * compatibility. The original four values (`classic` | `minimal` | `sidebar`
 * | `compact`) remain valid; premium experiences were added on top.
 *
 * The metadata and behaviour for each experience live in the central config
 * at `config/viewerExperiences.ts`.
 */
export type ViewerStyle =
    | 'classic'
    | 'minimal'
    | 'sidebar'
    | 'compact'
    | 'luxury'
    | 'story'
    | 'estate_agent'
    | 'presentation';

// Re-exported from the central config so existing importers keep working.
export { VIEWER_STYLES, VIEWER_STYLE_PRESETS } from './config/viewerExperiences';

/** Sound played when moving between scenes. 'off' = no sound, 'custom' = uploaded mp3 URL. */
export type SceneChangeSoundId = 'off' | 'sound_1' | 'sound_2' | 'sound_3' | 'sound_4' | 'custom';

export const SCENE_CHANGE_SOUNDS: { id: SceneChangeSoundId; label: string; filename: string }[] = [
    { id: 'off', label: 'Off', filename: '' },
    { id: 'sound_1', label: 'Positive chord (low)', filename: 'ES_Button, Positive, Chord, Low - Epidemic Sound.mp3' },
    { id: 'sound_2', label: 'Chord up down (soft)', filename: 'ES_Button, Chord, Up Down, Soft - Epidemic Sound.mp3' },
    { id: 'sound_3', label: 'Chord up (straight)', filename: 'ES_Button, Chord, Up, Straight - Epidemic Sound.mp3' },
    { id: 'sound_4', label: 'Short tonal (tone up)', filename: 'ES_Button, Short, Tonal, Soft, Tone Up - Epidemic Sound.mp3' },
];

/** Background music track while viewing the tour. 'off' = no music, 'custom' = uploaded mp3 URL. */
export type BackgroundMusicTrackId = 'off' | 'track_1' | 'track_2' | 'track_3' | 'track_4' | 'track_5' | 'track_6' | 'track_7' | 'track_8' | 'track_9' | 'track_10' | 'custom';

export const BACKGROUND_MUSIC_TRACKS: { id: BackgroundMusicTrackId; label: string; filename: string }[] = [
    { id: 'off', label: 'Off', filename: '' },
    { id: 'track_1', label: 'Comfortable under the Surface - Hanna Lindgren', filename: 'ES_Comfortable under the Surface - Hanna Lindgren.mp3' },
    { id: 'track_2', label: 'Crazy (Instrumental Version) - Chris Coral', filename: 'ES_Crazy (Instrumental Version) - Chris Coral.mp3' },
    { id: 'track_3', label: 'Drops - HATAMITSUNAMI', filename: 'ES_Drops - HATAMITSUNAMI.mp3' },
    { id: 'track_4', label: 'Keep Your Focus on Me (Instrumental Version) - Basixx', filename: 'ES_Keep Your Focus on Me (Instrumental Version) - Basixx.mp3' },
    { id: 'track_5', label: 'Low Orbit - Jobii', filename: 'ES_Low Orbit - Jobii.mp3' },
    { id: 'track_6', label: 'Says Dava - By Lotus', filename: 'ES_Says Dava - By Lotus.mp3' },
    { id: 'track_7', label: 'Steps To Mindfulness - Calm Shores', filename: 'ES_Steps To Mindfulness - Calm Shores.mp3' },
    { id: 'track_8', label: 'The Crimson Tide - Calm Shores', filename: 'ES_The Crimson Tide - Calm Shores.mp3' },
    { id: 'track_9', label: 'Where Is My Coffee - Daniel Fridell', filename: 'ES_Where Is My Coffee - Daniel Fridell.mp3' },
    { id: 'track_10', label: 'alma - bomull', filename: 'ES_alma - bomull.mp3' },
];

export interface TourSettings {
    autoRotate: boolean;
    autoRotateSpeed: number;
    compass: boolean;
    defaultFov: number;
    minFov: number;
    maxFov: number;
    mouseZoom: boolean;
    showControls: boolean;
    brandingEnabled: boolean;
    hotspotStyle: HotspotStylePreset;
    hotspotColor: string;
    /** 'default' = arrow/door use brand icon; 'chosen' = use icon selected per hotspot */
    hotspotIconMode?: HotspotIconMode;
    // Scene transition animation (moving between panoramas)
    transitionEffect?: TransitionEffect;
    transitionSpeed?: number; // milliseconds
    transitionRotation?: boolean; // rotate camera toward target during transition
    /** When true, zoom toward hotspot before dissolving to next scene; when false, dissolve only (no zoom). */
    transitionZoom?: boolean;
    // Guided tour
    guidedStops?: string[];
    guidedInterval?: number;
    // Lead capture
    leadCaptureEnabled?: boolean;
    leadCaptureDelay?: number; // seconds before showing lead form
    // Contact / enquiry channels — let interested viewers reach the agent
    // directly (request a call, WhatsApp, email). When enabled and at least
    // one channel is configured, the public viewer shows a "Contact" button.
    contactEnabled?: boolean;
    /** Display name shown in the viewer contact panel (agent or agency). */
    contactName?: string;
    /** Phone number used for the "Request a call" callback form context. */
    contactPhone?: string;
    /**
     * WhatsApp contact. Either a phone number in international format
     * (e.g. +44 7911 123456) which the viewer turns into a wa.me link, or
     * a full WhatsApp link (wa.me / wa.link / chat.whatsapp.com).
     */
    contactWhatsApp?: string;
    /** Email address used for the "Send an email" mailto link. */
    contactEmail?: string;
    /**
     * Where to email new enquiry alerts (e.g. call-back requests). Falls
     * back to the tour owner's account email when blank.
     */
    contactNotifyEmail?: string;
    // Analytics
    gtmId?: string; // Google Tag Manager container ID
    // Sound effect when moving between scenes (point-to-point). One of SceneChangeSoundId; 'off' or undefined = no sound.
    sceneChangeSound?: SceneChangeSoundId;
    // Custom scene-change MP3 URL (used when sceneChangeSound === 'custom').
    customSceneChangeSoundUrl?: string;
    // Background music while viewing. One of BackgroundMusicTrackId; 'off' or undefined = no music.
    backgroundMusicTrack?: BackgroundMusicTrackId;
    // Custom background music MP3 URL (used when backgroundMusicTrack === 'custom').
    customBackgroundMusicUrl?: string;
    // Hide description text on the public splash/loading screen.
    hideAddressOnSplash?: boolean;
    // When true (default), hide the nadir patch at the bottom of the panorama.
    // Set false to show the brand/tripod cover.
    excludeNadirPatch?: boolean;
    /**
     * Which 360° variant the public/shared viewer should open with for scenes
     * that have a staged version. The toggle button still lets the audience
     * switch in either direction at runtime; this just sets the default the
     * tour owner wants to lead with. Undefined = 'original'.
     */
    defaultStagedView?: 'original' | 'staged';
    /**
     * How variant transitions play. See `VariantPlaybackMode`. Undefined
     * is treated as 'manual'.
     */
    variantPlaybackMode?: VariantPlaybackMode;
    /**
     * Default duration (seconds) for the transition timeline. Allowed
     * values: 5, 10, 15, 20, 30. Falls back to 15 when missing.
     */
    variantPlaybackDurationSec?: 5 | 10 | 15 | 20 | 30;
    /**
     * Virtual Agent Guide mode. Mutually exclusive top-level switch:
     *   - 'off'        : no agent UI in the public viewer.
     *   - 'per_scene'  : per-scene agent hotspots open a transient
     *                    overlay when clicked (legacy default; kept
     *                    when undefined for back-compat).
     *   - 'tour_wide'  : a single continuous narration plays across
     *                    the whole tour via a persistent mini-player.
     *
     * Switching modes is non-destructive — both `scene_guides` and
     * `tour_guides` rows persist in the DB regardless. The viewer
     * just renders whichever the active mode dictates.
     */
    agent_mode?: AgentMode;
    /**
     * Agent Mode 2.0 global settings (default presenter, autoplay,
     * captions/transcript/CTA toggles, default voice/language/orb style).
     * See `TourAgentSettings`. Read via `getTourAgentSettings()`.
     */
    agent?: TourAgentSettings;
    /** Public viewer chrome layout. See `ViewerStyle` and `resolveViewerStyle()`. */
    viewerStyle?: ViewerStyle;
}

export interface CustomBranding {
    show_rehub_watermark: boolean;
}

export type SceneStagedStatus = 'idle' | 'processing' | 'complete' | 'failed';

export interface SceneStagedMeta {
    /** v1 schema version. Bump if the meta shape changes incompatibly. */
    version: 1;
    scope: 'indoor' | 'outdoor';
    /** Indoor only — id from `@rehub/staging-core` ROOM_TYPES. */
    room_type?: string;
    /** Outdoor only — id from `@rehub/staging-core` OUTDOOR_SPACE_TYPES. */
    outdoor_space_type?: string;
    /** Style id (FURNITURE_STYLES for indoor, OUTDOOR_STYLES for outdoor). */
    style: string;
    /** Optional free-text the user appended to the base prompt. */
    custom_prompt?: string;
    /** True when REMOVE_TRIPOD_360_PROMPT was run as a pre-pass. */
    tripod_removal_applied: boolean;
    /** Gemini model id used; pinned for reproducibility / regression triage. */
    model: string;
    /** Total credits charged through `process-image` for this variant (1 base + 1 if tripod). */
    credits_charged: number;
    /** Audit pointer to the staging generation row created by `process-image`. */
    generation_id?: string;
    /** Audit pointer to the tripod-removal generation, when applicable. */
    tripod_generation_id?: string;
    /** Set only when staged_status = 'failed'. Cleared on retry. */
    error?: string;
    /**
     * User-facing variant name. Mirrors `scene_staged_variants.label` for the
     * legacy single-variant cache; safe to omit. Used by viewer UIs that
     * don't yet load the variants table.
     */
    label?: string;
}

/**
 * One named AI-staged version of a scene's 360° pano. A scene can have
 * many of these (e.g. "Modern", "Scandi", "Family-friendly"); the viewer
 * lets the audience cycle between the original and the variants.
 *
 * Persisted in `scene_staged_variants` (migration 20260424100000).
 */
export interface SceneStagedVariant {
    id: string;
    scene_id: string;
    user_id: string;
    label: string;
    image_url: string;
    thumbnail_url: string | null;
    meta: SceneStagedMeta | Record<string, unknown> | null;
    is_default: boolean;
    /** Variant the transition timeline opens with on the LEFT (From) side. At most one true per scene. */
    is_default_from: boolean;
    /** Variant the transition timeline opens with on the RIGHT (To) side. At most one true per scene. */
    is_default_to: boolean;
    position: number;
    created_at: string;
}

/**
 * How variant transitions play in the public viewer.
 *   manual — user must open the timeline and press play (default).
 *   auto   — on scene entry with 2+ variants, auto-open the timeline
 *            and play From → To once using the scene's defaults.
 *   loop   — same as auto but ping-pongs continuously.
 */
export type VariantPlaybackMode = 'manual' | 'auto' | 'loop';

/**
 * How a scene should be rendered. Image and video scenes share the same
 * navigation, hotspots, branding and analytics — only the render engine
 * (image texture vs video texture) differs.
 */
export type SceneMediaType = 'image' | 'video';

/**
 * Probed metadata for a 360° video scene, read client-side from the MP4 at
 * upload time (no server transcode). Used to surface duration/resolution/size
 * in the studio and to inform future adaptive-quality decisions.
 */
export interface SceneVideoMeta {
    /** Video duration in seconds. */
    durationSec: number;
    /** Native video width in pixels (equirectangular, ~2:1). */
    width: number;
    /** Native video height in pixels. */
    height: number;
    /** Uploaded file size in bytes. */
    sizeBytes: number;
    /** Best-effort codec name (e.g. 'h264', 'h265') or MIME hint. Optional. */
    codec?: string;
    /** width / height at analysis time. Optional (older rows). */
    aspectRatio?: number;
    /** True when the frame is ~2:1 (equirectangular). Optional. */
    is2to1?: boolean;
    /** True when the MP4 carries spherical/360 metadata. Optional. */
    hasSphericalMetadata?: boolean;
    /** True when the video has an audio track. Optional. */
    hasAudio?: boolean;
}

/** How a scene/live video is shown in the 2D and XR viewers. */
export type SceneVideoDisplayMode = 'immersive' | 'floating';

/** Corner placement for the floating flat video player. */
export type SceneVideoFloatingPosition =
    | 'bottom-left'
    | 'bottom-right'
    | 'top-left'
    | 'top-right';

/** Relative size of the floating flat video player. */
export type SceneVideoFloatingSize = 'small' | 'medium' | 'large';

/**
 * Creator-configurable playback controls for a 360° video scene. Persisted
 * on `scenes.video_settings`. Defaults favour autoplay + sound on; browsers
 * may still block unmuted autoplay until the first user gesture.
 */
export interface SceneVideoSettings {
    /** Start playing automatically when the scene loads. Default true. */
    autoplay: boolean;
    /** Loop the video continuously. Default true. */
    loop: boolean;
    /** Start muted. Default false (sound on). */
    muted: boolean;
    /** Playback volume 0..1 when unmuted. Default 1. */
    volume: number;
    /** Seek to this time (seconds) on scene entry. Default 0. */
    startTimeSec: number;
    /** Immersive 360° on the sphere, or a floating flat PiP player. Default immersive. */
    displayMode: SceneVideoDisplayMode;
    /** Corner for the floating PiP. Default bottom-right. */
    floatingPosition: SceneVideoFloatingPosition;
    /** Relative size of the floating player. Default medium. */
    floatingSize: SceneVideoFloatingSize;
}

/** Default playback settings applied to a newly-uploaded video scene. */
export const DEFAULT_SCENE_VIDEO_SETTINGS: SceneVideoSettings = {
    autoplay: true,
    loop: true,
    muted: false,
    volume: 1,
    startTimeSec: 0,
    displayMode: 'immersive',
    floatingPosition: 'bottom-right',
    floatingSize: 'medium',
};

const FLOATING_POSITIONS: readonly SceneVideoFloatingPosition[] = [
    'bottom-left',
    'bottom-right',
    'top-left',
    'top-right',
];
const FLOATING_SIZES: readonly SceneVideoFloatingSize[] = ['small', 'medium', 'large'];

/**
 * Merge partial / legacy `video_settings` rows with current defaults so older
 * scenes missing `displayMode` / floating keys still render safely.
 */
export const normalizeSceneVideoSettings = (
    raw?: Partial<SceneVideoSettings> | null,
): SceneVideoSettings => {
    const base = { ...DEFAULT_SCENE_VIDEO_SETTINGS, ...(raw ?? {}) };
    const displayMode: SceneVideoDisplayMode =
        base.displayMode === 'floating' ? 'floating' : 'immersive';
    const floatingPosition = FLOATING_POSITIONS.includes(base.floatingPosition)
        ? base.floatingPosition
        : DEFAULT_SCENE_VIDEO_SETTINGS.floatingPosition;
    const floatingSize = FLOATING_SIZES.includes(base.floatingSize)
        ? base.floatingSize
        : DEFAULT_SCENE_VIDEO_SETTINGS.floatingSize;
    return {
        autoplay: Boolean(base.autoplay),
        loop: Boolean(base.loop),
        muted: Boolean(base.muted),
        volume: Math.min(1, Math.max(0, Number(base.volume) || 0)),
        startTimeSec: Math.max(0, Number(base.startTimeSec) || 0),
        displayMode,
        floatingPosition,
        floatingSize,
    };
};

export interface Scene {
    id: string;
    tour_id: string;
    title: string;
    image_url: string;
    thumbnail_url: string | null;
    /**
     * 'image' (default) or 'video'. See SceneMediaType.
     *
     * The `video_*` fields below serve a DUAL purpose:
     *   - media_type='video': this scene IS a 360° video (still image_url is
     *     just the poster). Legacy standalone video scene.
     *   - media_type='image' + video_url set: an image scene (keeps its
     *     educational hotspots) with an optional LIVE-VIDEO COMPANION the
     *     viewer can toggle on to "bring the scene to life", then off again.
     *     See {@link hasLiveVideoCompanion}.
     */
    media_type: SceneMediaType;
    /** Equirectangular MP4 URL. The video for a standalone video scene, or the live-video companion of an image scene; null when neither. */
    video_url: string | null;
    /** Auto-generated poster frame URL for the video (mirrors image_url for standalone video scenes). */
    video_poster_url: string | null;
    /** Probed video metadata (duration/resolution/size). Null when there is no video. */
    video_meta: SceneVideoMeta | null;
    /** Creator playback controls for the video. Null when there is no video. */
    video_settings: SceneVideoSettings | null;
    is_start_scene: boolean;
    /**
     * Per-scene access lock. In the public viewer, locked scenes only open for
     * entitled visitors (360Worlds subscribers, access-link / tour-password
     * holders, the owner / admins). On a subscription 360World, locking some
     * scenes turns the whole-tour paywall into a free-preview mode where the
     * unlocked scenes are explorable and locked ones prompt the subscribe modal.
     */
    is_locked: boolean;
    order: number;
    /** Zone this scene belongs to (null = "Unsorted"). See Zone. */
    zone_id: string | null;
    initial_yaw: number;
    initial_pitch: number;
    initial_hfov: number;
    /** Camera/tripod height above the floor in metres, for the editor measurement tool. null = unknown (UI defaults to 1.5). */
    camera_height_m: number | null;
    ai_room_type: string | null;
    ai_room_label: string | null;
    ai_confidence: number | null;
    ai_features: any | null;
    ai_processed_at: string | null;
    /** AI-detected floor: -1 basement, 0 ground, 1 first… null = unknown. */
    ai_floor_level: number | null;
    ai_floor_confidence: number | null;
    /** Persisted doorway detections from the auto-link pipeline. */
    ai_doorways: SceneDoorway[] | null;
    created_at: string;
    /** When the pano was taken (EXIF). Used for AI auto-link: scenes shot close in time are likely adjacent. */
    capture_time: string | null;
    // === 360° AI-staged variant (v1: binary, one variant per scene) ===
    staged_image_url: string | null;
    staged_thumbnail_url: string | null;
    staged_status: SceneStagedStatus;
    staged_meta: SceneStagedMeta | null;
    staged_at: string | null;
    /**
     * Per-scene background music override. When null/undefined the scene
     * inherits the tour-wide `TourSettings.backgroundMusicTrack`. 'off' means
     * silence while this scene is active; any other track id (or 'custom' with
     * `custom_background_music_url`) overrides the tour default for this scene.
     * See {@link BackgroundMusicTrackId}.
     */
    background_music_track: BackgroundMusicTrackId | null;
    /** Custom per-scene background music MP3 URL (used when background_music_track === 'custom'). */
    custom_background_music_url: string | null;
}

/**
 * True when this is an IMAGE scene that has a live-video companion attached
 * (i.e. the viewer should offer a "bring the scene to life" toggle that swaps
 * the still panorama for `video_url` and back). Standalone video scenes
 * (`media_type === 'video'`) are NOT companions — they are always video.
 */
export const hasLiveVideoCompanion = (
    scene: Pick<Scene, 'media_type' | 'video_url'>,
): boolean => scene.media_type === 'image' && !!scene.video_url;

/**
 * A named, ordered group of scenes within a tour. Lets large venues
 * (hotels, wedding venues) be split into manageable areas. One zone per
 * scene via `Scene.zone_id`; zones are independent of floor plans.
 */
export interface Zone {
    id: string;
    tour_id: string;
    title: string;
    /** Optional hex color for the zone chip/header. */
    color: string | null;
    order: number;
    created_at: string;
}

/** Doorway detection persisted on `scenes.ai_doorways`. */
export interface SceneDoorway {
    id: string;
    type: string;
    description: string;
    x_percent: number;
    y_percent: number;
    leads_to_guess: string;
    confidence: number;
    fingerprint?: Record<string, string>;
}

// ============================================================
// AI LINKING v2 — predictions, review workflow & learning
// ============================================================

/** Per-factor breakdown of a link prediction's composite confidence. */
export interface LinkFactorScores {
    visual: number;
    room_logic: number;
    sequence: number;
    time: number;
    graph: number;
    floor_penalty: number;
    composite: number;
}

export type LinkPredictionStatus =
    | 'auto_created'
    | 'suggested'
    | 'flagged'
    | 'accepted'
    | 'rejected'
    | 'changed';

export interface TourLinkPrediction {
    id: string;
    tour_id: string;
    job_id: string | null;
    from_scene_id: string;
    to_scene_id: string;
    from_doorway_id: string | null;
    to_doorway_id: string | null;
    from_yaw: number | null;
    from_pitch: number | null;
    from_target_yaw: number | null;
    to_yaw: number | null;
    to_pitch: number | null;
    to_target_yaw: number | null;
    confidence: number;
    factors: LinkFactorScores | null;
    reason: string | null;
    source: string | null;
    alternatives: { to_scene_id: string; confidence: number }[] | null;
    status: LinkPredictionStatus;
    forward_hotspot_id: string | null;
    reverse_hotspot_id: string | null;
    reviewed_at: string | null;
    created_at: string;
}

/** Venue Mode hotspot types (Phase 1). */
export type VenueHotspotType =
    | 'shop'
    | 'restaurant'
    | 'event'
    | 'offer'
    | 'amenity'
    | 'information'
    | 'accessibility'
    | 'parking'
    | 'transport'
    | 'sponsored';

/** Hospitality Mode hotspot types. */
export type HospitalityHotspotType =
    | 'table'
    | 'menu'
    | 'special'
    | 'bar'
    | 'private_hire'
    | 'booking'
    | 'sport';

// Adventure Mode types live in apps/tours/adventure/types.ts and are
// re-exported here so components can keep importing from '../types'.
export type {
    AdventureHotspotType,
    HotspotAdventureMeta,
    AdventureCondition,
    AdventureAction,
    AdventureEvent,
    AdventureSessionState,
    AdventureConfig,
    AdventureRoom,
    AdventurePuzzle,
    AdventurePuzzleType,
    AdventureItem,
    AdventureItemCategory,
    AdventureAchievement,
    AdventureVariable,
    AdventureBundle,
    AdventureEnding,
    AdventureHudSettings,
    AdventureDifficulty,
} from './adventure/types';
import type { AdventureHotspotType } from './adventure/types';

export type HotspotType = 'scene' | 'info' | 'link' | 'video' | 'agent' | VenueHotspotType | HospitalityHotspotType | AdventureHotspotType;

export interface Hotspot {
    id: string;
    scene_id: string;
    type: HotspotType;
    title: string | null;
    yaw: number;
    pitch: number;
    target_scene_id: string | null;
    target_yaw: number | null;
    info_text: string | null;
    link_url: string | null;
    icon: string;
    /** When icon is 'custom', this URL points to the uploaded image. */
    custom_icon_url: string | null;
    css_class: string | null;
    /** Venue Mode: links the hotspot to a directory item (shop/restaurant). */
    venue_directory_item_id: string | null;
    ai_generated: boolean;
    ai_confidence: number | null;
    user_confirmed: boolean;
    /**
     * Free-form JSONB. Info hotspots store optional voice narration here
     * under `meta.voice` (see HotspotVoiceSettings) — a lightweight
     * "read it aloud" that reuses the Agent Mode TTS pipeline.
     */
    meta: Record<string, any>;
    created_at: string;
}

// ------------------------------------------------------------
// Info hotspot voice narration (meta.voice)
// ------------------------------------------------------------

/**
 * One cached, playable narration track for an info hotspot, keyed by
 * ISO 639-1 language code inside `HotspotVoiceSettings.tracks`. Generated
 * by the `tours-generate-narration` edge function (scope='hotspot').
 */
export interface HotspotVoiceTrack {
    /** The text that was spoken (defaults to the hotspot's info_text for the primary language). */
    script_text: string;
    /** Public URL into the 'tours' bucket (null until generated). */
    audio_url: string | null;
    audio_path?: string | null;
    audio_hash?: string | null;
    audio_provider?: AgentVoiceProvider | null;
    audio_voice_id?: string | null;
    audio_duration_ms?: number | null;
    generated_at?: string | null;
}

/**
 * Optional voice narration config for an info hotspot. Persisted in
 * `hotspots.meta.voice`. Every language track (including the primary) is
 * stored under `tracks`, keyed by ISO 639-1 code.
 */
export interface HotspotVoiceSettings {
    /** Whether "read aloud" is offered in the public viewer for this hotspot. */
    enabled: boolean;
    /** ElevenLabs / fal.ai voice id used to generate the narration. */
    voice_id: string | null;
    provider: AgentVoiceProvider;
    /** Speak automatically when the info popover opens (subject to the browser first-gesture rule). */
    autoplay: boolean;
    /** ISO 639-1 code of the primary/default language. */
    primary_language: string;
    /** Per-language narration tracks, keyed by ISO 639-1 code. */
    tracks: Record<string, HotspotVoiceTrack>;
}

// ============================================================
// VIRTUAL AGENT GUIDE
// ============================================================

export type AgentPresenterMode = 'image_voice' | 'video' | 'ai_avatar' | 'voice_orb' | 'recorded_guide';
export type AgentVoiceProvider = 'elevenlabs' | 'falai';

/**
 * Top-level switch on a tour deciding which agent experience the
 * public viewer renders. Persisted at `tours.settings.agent_mode`.
 *
 *   - 'off'        : no agent UI in the public viewer.
 *   - 'per_scene'  : per-scene agent hotspots open transient overlays
 *                    when clicked. Default when undefined (back-compat).
 *   - 'tour_wide'  : a single continuous narration plays across the
 *                    whole tour via a persistent mini-player.
 *
 * The two modes are mutually exclusive in the viewer; rows in both
 * `scene_guides` and `tour_guides` persist regardless of which mode
 * is currently active so toggling is non-destructive.
 */
export type AgentMode = 'off' | 'per_scene' | 'tour_wide';

// ------------------------------------------------------------
// Agent Mode 2.0 — orb styling, avatar config, overrides
// ------------------------------------------------------------

/** Visual treatment for the faceless `voice_orb` presenter mode. */
export type OrbStyle =
    | 'minimal_pulse'
    | 'liquid_gradient'
    | 'waveform'
    | 'glass_orb'
    | 'neon_ring'
    | 'luxury_gold'
    | 'brand_gradient'
    | 'subtle_transparent';

export const ORB_STYLES: OrbStyle[] = [
    'minimal_pulse',
    'liquid_gradient',
    'waveform',
    'glass_orb',
    'neon_ring',
    'luxury_gold',
    'brand_gradient',
    'subtle_transparent',
];

/** Human-readable labels for each orb style (UI pickers). */
export const ORB_STYLE_LABELS: Record<OrbStyle, string> = {
    minimal_pulse: 'Minimal pulse',
    liquid_gradient: 'Liquid gradient',
    waveform: 'Waveform',
    glass_orb: 'Glass orb',
    neon_ring: 'Neon ring',
    luxury_gold: 'Luxury gold',
    brand_gradient: 'Brand gradient',
    subtle_transparent: 'Subtle transparent',
};

/** Where the presenter / orb sits over the panorama. */
export type PresenterPosition = 'bottom-left' | 'bottom-right' | 'centre-bottom' | 'floating';

export const PRESENTER_POSITIONS: PresenterPosition[] = ['bottom-left', 'bottom-right', 'centre-bottom', 'floating'];

export const PRESENTER_POSITION_LABELS: Record<PresenterPosition, string> = {
    'bottom-left': 'Bottom left',
    'bottom-right': 'Bottom right',
    'centre-bottom': 'Centre bottom',
    floating: 'Floating',
};

/** How energetic the orb animation feels. */
export type OrbAnimationIntensity = 'subtle' | 'medium' | 'strong';

/**
 * Full visual configuration for a `voice_orb` presenter. Persisted in
 * `agent_presenters.meta.orb` (JSON). Every field is optional in storage;
 * `getOrbSettings()` fills defaults + back-compat from `accent_color`.
 */
export interface OrbSettings {
    style: OrbStyle;
    /** Primary hex tint. Falls back to presenter.accent_color → brand accent. */
    accent_color: string;
    /** Secondary hex tint used by gradient / dual-tone styles. */
    secondary_color: string;
    /** Relative scale of the orb, 0.6–1.4 (1 = default). */
    size: number;
    /** Overall opacity, 0.2–1. */
    opacity: number;
    position: PresenterPosition;
    intensity: OrbAnimationIntensity;
    /** Scale/glow reacts to narration playback time. */
    pulse_with_audio: boolean;
    /** Gentle idle motion when not playing. */
    idle_animation: boolean;
    /** Fade the orb in on open / out on close. */
    fade: boolean;
}

/** Source of an AI-avatar presenter's likeness (beta). */
export type AvatarSource = 'upload' | 'stock' | 'generated';

/** Backdrop treatment behind an AI avatar (beta). */
export type AvatarBackground = 'transparent' | 'blurred' | 'solid' | 'branded';

/**
 * AI-avatar configuration (beta / coming soon). Persisted in
 * `agent_presenters.meta.avatar` (JSON). No backend yet — the editor
 * captures these fields and the viewer falls back to image/orb.
 */
export interface AvatarSettings {
    source: AvatarSource;
    /** Free-text style direction (e.g. "corporate", "warm presenter"). */
    style: string;
    background: AvatarBackground;
    voice_id: string | null;
    language: string | null;
}

// ------------------------------------------------------------
// Recorded Guide (mode = 'recorded_guide')
// ------------------------------------------------------------

/** How a recorded guide is presented over the panorama in the viewer. */
export type RecordedGuideDisplay =
    | 'circle_bubble'
    | 'rounded_card'
    | 'portrait_card'
    | 'audio_orb'
    | 'hidden_until_play';

export const RECORDED_GUIDE_DISPLAYS: RecordedGuideDisplay[] = [
    'circle_bubble',
    'rounded_card',
    'portrait_card',
    'audio_orb',
    'hidden_until_play',
];

export const RECORDED_GUIDE_DISPLAY_LABELS: Record<RecordedGuideDisplay, string> = {
    circle_bubble: 'Circular video bubble',
    rounded_card: 'Rounded video card',
    portrait_card: 'Portrait presenter card',
    audio_orb: 'Audio-only orb',
    hidden_until_play: 'Hidden until play',
};

/** Whether the recorded clip carries picture (video) or sound only (audio). */
export type RecordedMediaType = 'video' | 'audio';

/**
 * A recorded media clip. For a recorded_guide presenter this IS the
 * narration — the viewer plays the media element directly (no separate
 * AI MP3). Stored under guide `meta.recorded` (per scene / tour) and,
 * for a reusable default, under presenter `meta.recorded_guide.default_media`.
 */
export interface RecordedMedia {
    /** Public URL into the 'tours' bucket. */
    media_url: string;
    /** Storage path under the 'tours' bucket — used to delete/replace. */
    media_path: string;
    media_type: RecordedMediaType;
    /** MIME of the stored file (e.g. video/webm, audio/mpeg). */
    mime: string | null;
    /** Clip duration in ms when known (probed client-side after capture/upload). */
    duration_ms: number | null;
    /** ISO timestamp the clip was recorded / uploaded. */
    recorded_at: string;
}

/**
 * Recorded-guide presenter configuration. Persisted in
 * `agent_presenters.meta.recorded_guide` (JSON). The reusable default
 * clip is optional — scenes/tours usually attach their own recording.
 */
export interface RecordedGuideSettings {
    display: RecordedGuideDisplay;
    position: PresenterPosition;
    /** Relative scale of the bubble/card, 0.6–1.4 (1 = default). */
    size: number;
    /** Optional reusable clip used when a scene/tour has no recording of its own. */
    default_media?: RecordedMedia | null;
}

/**
 * Per-scene overrides of the tour default presenter behaviour. Persisted
 * in `scene_guides.meta.overrides` (JSON). `script` + `captions` already
 * live in dedicated columns; these cover the rest.
 */
export interface SceneGuideOverrides {
    /** Override the tour default presenter for this scene only. */
    presenter_id?: string | null;
    /** Override the narration voice for this scene. */
    voice_id?: string | null;
    /** Override the narration language (ISO 639-1) for this scene. */
    language?: string | null;
    /** Override where the presenter sits in the viewer. */
    position?: PresenterPosition;
    /** Hide the presenter entirely for this scene. */
    hidden?: boolean;
    /**
     * Custom text for the in-world agent marker badge. Empty/undefined
     * falls back to the hotspot title (or "Listen"). Ignored when
     * `marker_use_name` is true.
     */
    marker_label?: string | null;
    /** When true, the in-world agent marker badge shows the presenter's name. */
    marker_use_name?: boolean;
}

/**
 * Tour-wide Agent Mode global settings. Persisted in
 * `tours.settings.agent` (JSON). These are the defaults the viewer
 * applies unless a scene overrides them.
 */
export interface TourAgentSettings {
    /** Default presenter id used when a scene doesn't pick its own. */
    default_presenter_id?: string | null;
    /** Auto-start narration (subject to the iOS first-gesture rule). */
    autoplay?: boolean;
    /** Show the play button entry pill in the viewer. */
    show_play_button?: boolean;
    /** Show captions under the presenter. */
    show_captions?: boolean;
    /** Offer a "transcript" panel of the full narration. */
    show_transcript?: boolean;
    /** Show a contact / enquiry CTA after narration completes. */
    show_cta?: boolean;
    /** Default narration language (ISO 639-1). */
    default_language?: string;
    /** Default narration voice id. */
    default_voice_id?: string | null;
    /** Default orb style for voice_orb presenters. */
    default_orb_style?: OrbStyle;
}

/**
 * Reusable virtual-agent presenter (face + voice). One presenter
 * can be attached to many `scene_guides` rows across one or more
 * tours owned by the same user / team.
 */
export interface AgentPresenter {
    id: string;
    user_id: string;
    name: string;
    mode: AgentPresenterMode;
    /** Cut-out PNG / WebP — used as the overlay portrait + hotspot avatar. */
    avatar_url: string | null;
    /** Short silent looping MP4 (mode = 'video'). */
    loop_video_url: string | null;
    /** ElevenLabs / fal.ai voice id used by default for any scene_guide that picks this presenter. */
    default_voice_id: string | null;
    default_provider: AgentVoiceProvider;
    /** e.g. "Senior Sales Director" — shown under the presenter name in the overlay. */
    title: string | null;
    /** Hex color used for the overlay accent line / waveform. Falls back to brand profile accent. */
    accent_color: string | null;
    meta: Record<string, any>;
    created_at: string;
    updated_at: string;
}

/**
 * Per-scene guide. Links a presenter to a script and caches the
 * pre-rendered MP3 produced by the `tours-generate-narration` edge
 * function.
 */
export interface SceneGuide {
    id: string;
    scene_id: string;
    presenter_id: string;
    /** Optional binding to a specific in-scene hotspot. NULL means launched only via the floating pill. */
    hotspot_id: string | null;
    script_text: string;
    captions_enabled: boolean;
    /** Public URL into the 'tours' bucket (or null when not yet generated). */
    audio_url: string | null;
    audio_path: string | null;
    audio_hash: string | null;
    audio_provider: AgentVoiceProvider | null;
    audio_voice_id: string | null;
    audio_duration_ms: number | null;
    audio_generated_at: string | null;
    autoplay_on_scene_enter: boolean;
    duck_bgm: boolean;
    meta: Record<string, any>;
    created_at: string;
    updated_at: string;
}

/** Convenience shape returned from the joined `loadSceneGuide` query: scene_guide + its presenter. */
export interface ResolvedSceneGuide {
    guide: SceneGuide;
    presenter: AgentPresenter;
}

/**
 * Tour-wide guide. Mirrors SceneGuide but operates at the tour level
 * — one continuous narration that survives scene transitions. Active
 * only when `tour.settings.agent_mode === 'tour_wide'`.
 */
export interface TourGuide {
    id: string;
    tour_id: string;
    presenter_id: string;
    script_text: string;
    captions_enabled: boolean;
    /** Public URL into the 'tours' bucket (or null when not yet generated). */
    audio_url: string | null;
    audio_path: string | null;
    audio_hash: string | null;
    audio_provider: AgentVoiceProvider | null;
    audio_voice_id: string | null;
    audio_duration_ms: number | null;
    audio_generated_at: string | null;
    /** When true, the splash "Start tour with audio guide" CTA auto-plays the narration on entry. */
    autoplay_on_tour_enter: boolean;
    duck_bgm: boolean;
    meta: Record<string, any>;
    created_at: string;
    updated_at: string;
}

/** Convenience shape: tour_guide row + its presenter. */
export interface ResolvedTourGuide {
    guide: TourGuide;
    presenter: AgentPresenter;
}

export interface TourFloorPlan {
    id: string;
    tour_id: string;
    title: string;
    image_url: string | null;
    generated_layout: any | null;
    order: number;
    created_at: string;
}

export interface TourFloorPlanMarker {
    id: string;
    floor_plan_id: string;
    scene_id: string;
    x_percent: number;
    y_percent: number;
    created_at: string;
}

export interface TourProcessingJob {
    id: string;
    tour_id: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    progress: number;
    current_step: string | null;
    result: any | null;
    error: string | null;
    warnings: any | null;
    credits_charged: number;
    started_at: string | null;
    completed_at: string | null;
    created_at: string;
}

export interface StudioBuildOrder {
    id: string;
    tour_id: string;
    user_id: string;
    currency: 'USD' | 'GBP';
    pano_count: number;
    base_price_cents: number;
    additional_price_cents: number;
    rapid_price_cents: number;
    total_price_cents: number;
    status: StudioBuildStatus;
    stripe_checkout_session_id?: string | null;
    stripe_payment_intent_id?: string | null;
    created_at: string;
    updated_at: string;
}

// ============================================================
// FLOOR PLAN ORDERS (managed production service)
// ============================================================

export type FloorPlanOrderStatus =
    | 'submitted'
    | 'in_production'
    | 'quality_check'
    | 'completed'
    | 'cancelled';

export type FloorPlanPropertyType = 'House' | 'Apartment' | 'Commercial' | 'Other';

export interface FloorPlanOrder {
    id: string;
    user_id: string;
    tour_id: string;
    property_address: string | null;
    number_of_floors: number;
    property_type: FloorPlanPropertyType | null;
    notes: string | null;
    status: FloorPlanOrderStatus;
    credits_charged: number;
    floorplan_pdf_url: string | null;
    floorplan_png_url: string | null;
    production_notes: string | null;
    created_at: string;
    updated_at: string;
    completed_at: string | null;
}

// ============================================================
// TEAMS & USER MANAGEMENT
// ============================================================

export type TeamMemberRole = 'owner' | 'admin' | 'member';

export type CustomDomainStatus = 'pending' | 'active' | 'error';

/** A DNS record the customer must add at their registrar for their custom domain. */
export interface CustomDomainDnsRecord {
    type: 'CNAME' | 'A' | 'TXT';
    name: string;
    value: string;
    reason?: string;
}

export interface Team {
    id: string;
    name: string;
    owner_id: string;
    custom_domain: string | null;
    custom_domain_status?: CustomDomainStatus | null;
    custom_domain_dns?: CustomDomainDnsRecord[] | null;
    custom_domain_checked_at?: string | null;
    created_at: string;
    updated_at: string;
}

export interface TeamMember {
    id: string;
    team_id: string;
    user_id: string;
    role: TeamMemberRole;
    created_at: string;
    email?: string; // joined from profiles when listing
}

export interface TeamInvite {
    id: string;
    team_id: string;
    email: string;
    role: 'admin' | 'member';
    invited_by: string;
    token: string | null;
    expires_at: string | null;
    created_at: string;
}

// ============================================================
// USER PROFILE
// ============================================================

export interface UserProfile {
    id: string;
    email: string;
    credits: number;
    subscription_tier?: 'standard' | 'whitelabel';
    /**
     * Legacy tier (free/pro/agency/business). Kept for backward compatibility +
     * the shared active-space pool. New code should prefer `plan_type`.
     */
    tours_subscription_tier?: ToursSubscriptionTier | null;
    /**
     * Active Spaces plan: credits (PAYG) | standard | sales_pro | ultra |
     * enterprise. Source of truth for feature gating and entitlements.
     */
    plan_type?: ToursPlanType | null;
    /** Cached entitlements (set by the Stripe webhook); live values come from get_tours_entitlements. */
    active_spaces_limit?: number | null;
    brand_limit?: number | null;
    subscription_status?: string | null;
    current_period_end?: string | null;
    custom_logo_url?: string | null;
    custom_domain?: string | null;
    created_at?: string;
    referral_code?: string;
}

export interface ReferralReward {
  id: string;
  bonus_credits: number;
  referrer_bonus_credits?: number;
  purchased_credits: number;
  created_at: string;
  referred_email: string;
  referred_name?: string;
  reward_kind?: string;
  status?: string;
}

export interface ReferralStats {
  success: boolean;
  referral_code: string;
  signup_count: number;
  friends_joined?: number;
  total_bonus_credits: number;
  credits_earned?: number;
  invite_base_url?: string;
  rewards: ReferralReward[];
}

// ============================================================
// APP STATE
// ============================================================

export enum AppState {
    INITIALIZING = 'INITIALIZING',
    UPLOAD = 'UPLOAD',
    DASHBOARD = 'DASHBOARD',
    TOUR_OVERVIEW = 'TOUR_OVERVIEW',
    SCENE_MANAGER = 'SCENE_MANAGER',
    HOTSPOT_EDITOR = 'HOTSPOT_EDITOR',
    PROCESSING = 'PROCESSING',
    PUBLIC_VIEWER = 'PUBLIC_VIEWER',
    EMBED_VIEWER = 'EMBED_VIEWER'
}

// ============================================================
// VIEWER CONFIG (for Photo Sphere Viewer)
// ============================================================

export interface ViewerConfig {
    container: HTMLElement | string;
    panorama: string;
    caption?: string;
    loadingImg?: string;
    navbar?: string[] | boolean;
    defaultYaw?: number;
    defaultPitch?: number;
    defaultZoomLvl?: number;
    minFov?: number;
    maxFov?: number;
    mousewheel?: boolean;
    mousewheelCtrlKey?: boolean;
    touchmoveTwoFingers?: boolean;
    fisheye?: boolean;
    plugins?: any[];
}

export interface MarkerConfig {
    id: string;
    position: { yaw: number; pitch: number };
    html?: string;
    tooltip?: string | { content: string; position: string };
    data?: {
        type: 'scene' | 'info' | 'link' | 'video';
        targetSceneId?: string;
        infoText?: string;
        linkUrl?: string;
    };
}

// ============================================================
// BRAND PROFILES — reusable client branding presets
// ============================================================

export type LogoPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type LogoSize = 'small' | 'medium' | 'large';

export interface BrandProfile {
    id: string;
    user_id: string;
    name: string;
    logo_url: string | null;
    logo_position?: LogoPosition; // corner in viewer: default top-left
    logo_size?: LogoSize; // small | medium | large for play screen and corner
    hotspot_style: HotspotStylePreset;
    hotspot_color: string;
    /** 'default' = arrow/door use brand logo; 'chosen' = use icon selected per hotspot */
    hotspot_icon_mode?: HotspotIconMode;
    primary_color: string;
    show_powered_by: boolean;
    custom_watermark_text: string | null;
    splash_heading: string | null;
    splash_subheading: string | null;
    nadir_image_url: string | null;
    is_default: boolean;
    created_at: string;
    updated_at: string;
}

// ============================================================
// VENUE MODE — Phase 1
// ============================================================
// Admin-built venue experiences (shopping centres, leisure
// destinations, museums, universities, hospitals, stadiums,
// visitor attractions). Activated when `tour.tour_mode === 'venue'`.

export type VenueType =
    | 'shopping'
    | 'hospitality'
    | 'leisure'
    | 'hotel'
    | 'education'
    | 'healthcare'
    | 'stadium'
    | 'attraction'
    | 'business'
    | 'other';

export const VENUE_TYPES: { id: VenueType; label: string }[] = [
    { id: 'shopping', label: 'Shopping' },
    { id: 'hospitality', label: 'Hospitality' },
    { id: 'leisure', label: 'Leisure' },
    { id: 'hotel', label: 'Hotel' },
    { id: 'education', label: 'Education' },
    { id: 'healthcare', label: 'Healthcare' },
    { id: 'stadium', label: 'Stadium' },
    { id: 'attraction', label: 'Attraction' },
    { id: 'business', label: 'Business' },
    { id: 'other', label: 'Other' },
];

/** Suggested directory categories (free text in the DB; these power the picker). */
export const DIRECTORY_CATEGORIES: string[] = [
    'Fashion',
    'Food & Drink',
    'Health & Beauty',
    'Technology',
    'Jewellery',
    'Homeware',
    'Entertainment',
    'Cinema',
    'Gym',
    'Services',
    'Supermarket',
    'Children & Family',
    'Events',
    'Other',
];

/** Amenity / venue-point types used by floor-plan pins (spec §6). */
export type VenueAmenityType =
    | 'entrance'
    | 'exit'
    | 'toilet'
    | 'accessible_toilet'
    | 'lift'
    | 'escalator'
    | 'stairs'
    | 'customer_service'
    | 'car_park_entrance'
    | 'ev_charging'
    | 'taxi_point'
    | 'bus_train_access'
    | 'baby_changing'
    | 'prayer_room'
    | 'seating_area';

export const VENUE_AMENITY_TYPES: { id: VenueAmenityType; label: string }[] = [
    { id: 'entrance', label: 'Entrance' },
    { id: 'exit', label: 'Exit' },
    { id: 'toilet', label: 'Toilets' },
    { id: 'accessible_toilet', label: 'Accessible toilets' },
    { id: 'lift', label: 'Lift' },
    { id: 'escalator', label: 'Escalator' },
    { id: 'stairs', label: 'Stairs' },
    { id: 'customer_service', label: 'Customer service desk' },
    { id: 'car_park_entrance', label: 'Car park entrance' },
    { id: 'ev_charging', label: 'EV charging' },
    { id: 'taxi_point', label: 'Taxi point' },
    { id: 'bus_train_access', label: 'Bus / train access' },
    { id: 'baby_changing', label: 'Baby changing' },
    { id: 'prayer_room', label: 'Prayer room' },
    { id: 'seating_area', label: 'Seating area' },
];

export type VenueDirectoryStatus = 'active' | 'inactive';

// ----- Hospitality Mode -----

export type HospitalityType =
    | 'pub'
    | 'restaurant'
    | 'bar'
    | 'cafe'
    | 'hotel'
    | 'wedding_venue'
    | 'private_dining'
    | 'food_hall'
    | 'event_space'
    | 'other';

export const HOSPITALITY_TYPES: { id: HospitalityType; label: string }[] = [
    { id: 'pub', label: 'Pub' },
    { id: 'restaurant', label: 'Restaurant' },
    { id: 'bar', label: 'Bar' },
    { id: 'cafe', label: 'Café' },
    { id: 'hotel', label: 'Hotel' },
    { id: 'wedding_venue', label: 'Wedding venue' },
    { id: 'private_dining', label: 'Private dining' },
    { id: 'food_hall', label: 'Food hall' },
    { id: 'event_space', label: 'Event space' },
    { id: 'other', label: 'Other' },
];

/** Suggested area categories for Hospitality Mode directory items. */
export const HOSPITALITY_AREA_CATEGORIES: string[] = [
    'Dining',
    'Bar',
    'Private room',
    'Event space',
    'Beer garden',
    'Terrace',
    'Function room',
    'Lounge',
    'Hotel facility',
    'Other',
];

/** Days of the week, keyed for the structured opening-hours schedule. */
export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

/** A single open/close window within a day (e.g. lunch or dinner service). */
export interface OpeningHoursPeriod {
    /** 24-hour "HH:MM". */
    open: string;
    /** 24-hour "HH:MM". */
    close: string;
}

/** A day's opening hours: closed, or one or more open/close periods. */
export interface OpeningHoursDay {
    closed: boolean;
    periods: OpeningHoursPeriod[];
}

/** Structured weekly opening hours, keyed by day. */
export type OpeningHoursStructured = Record<DayKey, OpeningHoursDay>;

export interface VenueProfile {
    id: string;
    tour_id: string;
    venue_name: string;
    venue_type: VenueType;
    venue_description: string;
    address: string;
    city: string;
    postcode: string;
    website_url: string;
    phone: string;
    /** Human-readable summary, generated from opening_hours_structured when present. */
    opening_hours: string;
    /** Per-day schedule (source of truth for the editor). Null = legacy text only. */
    opening_hours_structured: OpeningHoursStructured | null;
    parking_info: string;
    accessibility_info: string;
    public_transport_info: string;
    // --- Hospitality-specific (nullable; only used when tour_mode = 'hospitality') ---
    hospitality_type: HospitalityType | null;
    booking_url: string | null;
    menu_url: string | null;
    delivery_url: string | null;
    private_hire_url: string | null;
    instagram_url: string | null;
    facebook_url: string | null;
    tripadvisor_url: string | null;
    google_reviews_url: string | null;
    /** Label for the primary booking CTA, e.g. "Book a Table". */
    default_table_booking_cta: string | null;
    created_at: string;
    updated_at: string;
}

export type HospitalityTableStatus = 'active' | 'inactive';

export interface HospitalityTable {
    id: string;
    tour_id: string;
    table_number: string | null;
    table_name: string;
    capacity: number | null;
    area_name: string | null;
    description: string | null;
    scene_id: string | null;
    floorplan_pin_id: string | null;
    is_bookable: boolean;
    booking_url: string | null;
    status: HospitalityTableStatus;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

export type VenueNoticeType =
    | 'special'
    | 'offer'
    | 'event'
    | 'announcement'
    | 'menu'
    | 'alert'
    | 'sport'
    | 'happy_hour';

export const VENUE_NOTICE_TYPES: { id: VenueNoticeType; label: string }[] = [
    { id: 'special', label: 'Special' },
    { id: 'offer', label: 'Offer' },
    { id: 'event', label: 'Event' },
    { id: 'announcement', label: 'Announcement' },
    { id: 'menu', label: 'Menu' },
    { id: 'alert', label: 'Alert' },
    { id: 'sport', label: 'Sport' },
    { id: 'happy_hour', label: 'Happy hour' },
];

export type VenueNoticeStatus = 'active' | 'inactive';

export interface VenueNotice {
    id: string;
    tour_id: string;
    title: string;
    description: string | null;
    notice_type: VenueNoticeType;
    image_url: string | null;
    cta_label: string | null;
    cta_url: string | null;
    linked_scene_id: string | null;
    linked_floorplan_pin_id: string | null;
    linked_directory_item_id: string | null;
    valid_from: string | null;
    valid_until: string | null;
    is_featured: boolean;
    status: VenueNoticeStatus;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

// ----- Client Update Portal -----

/** Sections a client update token may be allowed to edit. */
export type ClientUpdateSection =
    | 'specials'
    | 'menu'
    | 'events'
    | 'opening_hours'
    | 'booking_links'
    | 'announcements';

export const CLIENT_UPDATE_SECTIONS: { id: ClientUpdateSection; label: string; description: string }[] = [
    { id: 'specials', label: 'Specials', description: 'Specials, offers, happy hour, sport' },
    { id: 'menu', label: 'Menu link', description: 'Menu URL / PDF link' },
    { id: 'events', label: 'Events', description: 'Event notices' },
    { id: 'opening_hours', label: 'Opening hours', description: 'Opening hours text' },
    { id: 'booking_links', label: 'Booking links', description: 'Booking, private hire, delivery URLs' },
    { id: 'announcements', label: 'Announcements', description: 'Temporary announcements' },
];

export interface ClientUpdateToken {
    id: string;
    tour_id: string;
    token_hash: string;
    label: string | null;
    allowed_sections: ClientUpdateSection[];
    pin_hash: string | null;
    expires_at: string | null;
    revoked_at: string | null;
    last_used_at: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

/** Countdown clock style shown to viewers on expiring access links. */
export type AccessCountdownStyle = 'minimal' | 'digital' | 'classic' | 'pill' | 'badge';

export const ACCESS_COUNTDOWN_STYLES: { id: AccessCountdownStyle; label: string; description: string }[] = [
    { id: 'pill', label: 'Pill', description: 'Rounded badge with clock icon' },
    { id: 'minimal', label: 'Minimal', description: 'Small text, top corner' },
    { id: 'digital', label: 'Digital', description: 'Monospace LED-style timer' },
    { id: 'classic', label: 'Classic', description: 'Circular progress ring' },
    { id: 'badge', label: 'Badge', description: 'Bold corner badge with accent' },
];

export const ACCESS_LINK_DURATION_PRESETS: { id: string; label: string; hours: number }[] = [
    { id: '1h', label: '1 hour', hours: 1 },
    { id: '2h', label: '2 hours', hours: 2 },
    { id: '4h', label: '4 hours', hours: 4 },
    { id: '6h', label: '6 hours', hours: 6 },
    { id: '12h', label: '12 hours', hours: 12 },
    { id: '24h', label: '24 hours', hours: 24 },
    { id: '48h', label: '48 hours', hours: 48 },
    { id: '7d', label: '7 days', hours: 168 },
    { id: '30d', label: '30 days', hours: 720 },
];

export interface TourAccessLink {
    id: string;
    tour_id: string;
    token_hash: string;
    label: string | null;
    password_hash: string | null;
    expires_at: string;
    revoked_at: string | null;
    last_used_at: string | null;
    countdown_enabled: boolean;
    countdown_style: AccessCountdownStyle;
    allow_extension_request: boolean;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

export interface ValidatedAccessLink {
    linkId: string;
    label: string | null;
    expiresAt: string;
    countdownEnabled: boolean;
    countdownStyle: AccessCountdownStyle;
    allowExtensionRequest: boolean;
}

export interface VenueDirectoryItem {
    id: string;
    tour_id: string;
    name: string;
    category: string;
    sub_category: string | null;
    description: string;
    logo_url: string | null;
    cover_image_url: string | null;
    website_url: string | null;
    phone: string | null;
    email: string | null;
    opening_hours: string | null;
    unit_number: string | null;
    floor_level: string | null;
    /** Optional jump-to scene in the 360 tour. */
    scene_id: string | null;
    /** Optional link to a floor-plan pin. */
    floorplan_pin_id: string | null;
    tags: string[];
    is_featured: boolean;
    is_sponsored: boolean;
    status: VenueDirectoryStatus;
    position: number;
    created_at: string;
    updated_at: string;
}

export type VenueFloorplanPinKind = 'directory' | 'amenity';

export interface VenueFloorplanPin {
    id: string;
    floor_plan_id: string;
    pin_kind: VenueFloorplanPinKind;
    directory_item_id: string | null;
    amenity_type: VenueAmenityType | null;
    label: string | null;
    category: string | null;
    scene_id: string | null;
    x_percent: number;
    y_percent: number;
    created_at: string;
    updated_at: string;
}
